import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SetPassword() {
  const token = new URLSearchParams(window.location.search).get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("loading");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/account-tokens/${token}`).then(() => setState("ready")).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/auth/account-tokens/${token}/set-password`, { password: pw });
      setUser(r.data.user);
      toast.success("Password set");
      navigate("/portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "This link is no longer valid");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-[#F4FFF9]"
      style={{ background: "radial-gradient(130% 90% at 50% 0%, #0D3B2D 0%, #071A13 45%, #030B08 100%)" }}>
      <div className="p-6"><img src={LOGO.wordmark} alt="ECOREXS" className="w-[160px] h-auto object-contain" /></div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md bg-white/[0.04] border border-[#67F2B3]/20 backdrop-blur-xl rounded-2xl p-8 animate-fade-up" data-testid="set-password-card">
          {state === "loading" && <div className="text-center py-6"><Loader2 className="h-8 w-8 text-[#67F2B3] mx-auto animate-spin" /></div>}
          {state === "invalid" && (
            <div className="text-center py-6">
              <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
              <h1 className="font-heading font-bold text-xl mt-4">Link not valid</h1>
              <p className="text-sm text-[#F4FFF9]/60 mt-2">This link is invalid or has expired. Please request a new one.</p>
              <Button onClick={() => navigate("/login")} className="mt-5 bg-[#67F2B3] text-[#071A13] font-semibold rounded-xl">Back to Login</Button>
            </div>
          )}
          {state === "ready" && (
            <form onSubmit={submit}>
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-[#67F2B3]/12 border border-[#67F2B3]/25 mb-5"><KeyRound className="h-6 w-6 text-[#67F2B3]" /></div>
              <h1 className="font-heading font-bold text-2xl">Set your password</h1>
              <p className="text-sm text-[#F4FFF9]/60 mt-2 mb-6">Choose a secure password (at least 8 characters).</p>
              <Label className="text-[#F4FFF9]/80">New Password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="set-password-input"
                className="bg-white/5 border-[#67F2B3]/20 text-white placeholder:text-white/30 focus:border-[#67F2B3]" placeholder="••••••••" />
              <Button type="submit" disabled={busy} data-testid="set-password-submit" className="w-full mt-6 bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold py-6 rounded-xl">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set Password & Continue"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
